// imageToolbarPlugin.js
// ProseMirror plugin view that shows an alignment toolbar above a selected figure node.
//
// Behaviour (mirrors Jira/Confluence):
//   - Shows when a NodeSelection on a `figure` node is active
//   - Toolbar floats above the figure with: Align Left | Align Center | Align Right
//   - Dispatches setNodeMarkup with the new `align` attr on click
//   - Hidden for all other selection types

import {Plugin, PluginKey, NodeSelection} from 'prosemirror-state';
import {createRoot} from 'react-dom/client';
import React from 'react';

import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

export const imageToolbarKey = new PluginKey('imageToolbar');

// ─── React component ─────────────────────────────────────────────────────────

function ImageToolbar({align, onAlign}) {
    const btn = (value, Icon, label) => (
        <Tooltip title={label} arrow key={value}>
            <IconButton
                size="small"
                onMouseDown={(e) => {
                    e.preventDefault();
                    onAlign(value);
                }}
                sx={{
                    bgcolor: align === value ? 'action.selected' : 'transparent',
                    color: align === value ? 'primary.main' : 'text.secondary',
                    borderRadius: 1,
                    p: 0.5,
                }}
            >
                <Icon fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    return (
        <Paper
            elevation={4}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                gap: 0,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={0}>
                {btn('left',   FormatAlignLeftIcon,   'Align left')}
                {btn('center', FormatAlignCenterIcon, 'Align center')}
                {btn('right',  FormatAlignRightIcon,  'Align right')}
            </Stack>
        </Paper>
    );
}

// ─── Plugin view class ────────────────────────────────────────────────────────

class ImageToolbarView {
    constructor(view) {
        this.view = view;

        this.container = document.createElement('div');
        this.container.className = 'pm-image-toolbar';
        this.container.style.cssText = `
            position: absolute;
            display: none;
            z-index: 100;
            pointer-events: auto;
            white-space: nowrap;
        `;

        this.root = createRoot(this.container);
        this._destroyed = false;

        view.dom.parentNode.appendChild(this.container);
        this.update(view, null);
    }

    _isFigureSelection(state) {
        return (
            state.selection instanceof NodeSelection &&
            state.selection.node?.type?.name === 'figure'
        );
    }

    _renderToolbar(align) {
        if (this._destroyed) return;
        this.root.render(
            React.createElement(ImageToolbar, {
                align: align || 'center',
                onAlign: (newAlign) => {
                    const {state, dispatch} = this.view;
                    if (!this._isFigureSelection(state)) return;
                    const {from} = state.selection;
                    const node = state.doc.nodeAt(from);
                    if (!node) return;
                    dispatch(state.tr.setNodeMarkup(from, null, {...node.attrs, align: newAlign}));
                    this.view.focus();
                },
            })
        );
    }

    update(view, lastState) {
        this.view = view;
        const {state} = view;

        if (lastState?.doc.eq(state.doc) && lastState?.selection.eq(state.selection)) return;

        if (!this._isFigureSelection(state)) {
            this.container.style.display = 'none';
            return;
        }

        const {from} = state.selection;
        const node = state.doc.nodeAt(from);
        if (!node) {
            this.container.style.display = 'none';
            return;
        }

        this._renderToolbar(node.attrs.align || 'center');
        this.container.style.display = '';
        this._positionToolbar(view, from);
    }

    _positionToolbar(view, from) {
        // Anchor to the media element (<img>/<video>/<audio>) inside the figure,
        // not the full figure width, so the toolbar floats above the actual image.
        const figureDOM = view.nodeDOM(from);
        if (!figureDOM || !(figureDOM instanceof HTMLElement)) return;

        const mediaEl = figureDOM.querySelector('.pm-figure-media') || figureDOM;
        const mediaRect = mediaEl.getBoundingClientRect();
        const parentRect = this.container.offsetParent?.getBoundingClientRect();
        if (!parentRect) return;

        // Re-render first so getBoundingClientRect gives accurate toolbar size
        const {width: toolbarW, height: toolbarH} = this.container.getBoundingClientRect();

        const GAP = 6;
        const left = mediaRect.left - parentRect.left + mediaRect.width / 2 - toolbarW / 2;
        const top  = mediaRect.top  - parentRect.top  - toolbarH - GAP;

        this.container.style.left = `${Math.max(4, left)}px`;
        this.container.style.top  = `${Math.max(4, top)}px`;
    }

    destroy() {
        this._destroyed = true;
        const root = this.root;
        const container = this.container;
        queueMicrotask(() => {
            root.unmount();
            container.remove();
        });
    }
}

// ─── Plugin factory ───────────────────────────────────────────────────────────

export function createImageToolbarPlugin() {
    return new Plugin({
        key: imageToolbarKey,
        view(editorView) {
            return new ImageToolbarView(editorView);
        },
    });
}
